import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { ref, get, set } from 'firebase/database';
import SEO from '../components/SEO';
import MediaPicker from '../components/MediaPicker';

interface QuickReply { id: string; title: string; payload: string; }
interface ListRow { id: string; title: string; payload: string; }
interface LinkButton { id: string; title: string; url: string; type?: 'web_url' | 'phone_number'; }
interface GenericCard { id: string; title: string; subtitle?: string; imageUrl?: string; defaultActionUrl?: string; buttons: LinkButton[]; }
interface AskFollowUp { id: string; type: 'text' | 'buttons' | 'list'; content: string; buttons?: QuickReply[]; listRows?: ListRow[]; }
interface ApiHeader { key: string; value: string; }
interface CustomField { id: string; label: string; path: string; emoji?: string; }
interface DynamicResponseConfig {
  apiUrl?: string; apiMethod?: 'GET' | 'POST'; apiBody?: string; apiHeaders?: ApiHeader[];
  imagePath?: string; titlePath?: string; categoryPath?: string; metaPath?: string; metaLabel?: string; descriptionPath?: string;
  customFields?: CustomField[];
  button1Title?: string; button1Payload?: string;
  button2Title?: string; button2Payload?: string;
  button3Title?: string; button3Payload?: string;
  fallbackMessage?: string;
}
interface OrderHandlerConfig {
  collectName?: boolean; collectPhone?: boolean; collectEmail?: boolean; collectAddress?: boolean;
  askQuantity?: boolean; quantityLabel?: string;
  action?: 'payment_link' | 'admin_notification' | 'api_call' | 'custom_message';
  paymentLinkUrl?: string; paymentLinkLabel?: string;
  adminPhone?: string; adminMessage?: string;
  apiUrl?: string; apiMethod?: 'GET' | 'POST'; apiBody?: string;
  successMessage?: string; errorMessage?: string;
  customMessage?: string; confirmationMessage?: string;
}
interface ResponseItem {
  id: string;
  type: 'text' | 'image' | 'buttons' | 'list' | 'link' | 'generic_template' | 'request_agent' | 'ask' | 'webhook' | 'chain' | 'dynamic_list' | 'dynamic_response' | 'order_handler';
  content: string;
  buttons?: QuickReply[];
  listRows?: ListRow[];
  url?: string;
  linkLabel?: string;
  linkButtons?: LinkButton[];
  cards?: GenericCard[];
  askVariable?: string;
  askResponses?: AskFollowUp[];
  webhookMethod?: 'GET' | 'POST';
  webhookBody?: string;
  webhookResponsePath?: string;
  webhookSuccessMessage?: string;
  webhookErrorMessage?: string;
  chainTrigger?: string;
  chainDelay?: number;
  apiUrl?: string;
  apiMethod?: 'GET' | 'POST';
  apiBody?: string;
  apiHeaders?: ApiHeader[];
  itemTitlePath?: string;
  itemPayloadPath?: string;
  listTitle?: string;
  dynamicResponseConfig?: DynamicResponseConfig;
  orderHandlerConfig?: OrderHandlerConfig;
}
interface FacebookTrigger { trigger: string; responses: ResponseItem[]; }
interface MenuItem { id: string; title: string; payload: string; type: 'postback' | 'web_url'; url?: string; }

// Migrates the old single-response shape ({ trigger, response, buttons }) —
// still sitting in Firebase for any bot built before the builder matched the
// backend's flow.triggers[].responses[] schema — into the current shape so
// existing bots' replies keep working after this upgrade instead of going
// silently blank the way they were before it.
function migrateTrigger(raw: any): FacebookTrigger {
  if (Array.isArray(raw.responses)) return { trigger: raw.trigger, responses: raw.responses };
  const hasButtons = Array.isArray(raw.buttons) && raw.buttons.length > 0;
  const resp: ResponseItem = hasButtons
    ? { id: '1', type: 'buttons', content: raw.response || '', buttons: raw.buttons }
    : { id: '1', type: 'text', content: raw.response || '' };
  return { trigger: raw.trigger, responses: [resp] };
}

const RESPONSE_TYPES: Array<{ id: ResponseItem['type']; label: string }> = [
  { id: 'text', label: '💬 Text' },
  { id: 'image', label: '🖼️ Image' },
  { id: 'buttons', label: '🔘 Quick Replies' },
  { id: 'list', label: '📋 List' },
  { id: 'link', label: '🔗 Link Button' },
  { id: 'generic_template', label: '🎠 Carousel' },
  { id: 'request_agent', label: '🙋 Talk to a Human' },
  { id: 'ask', label: '❓ Ask & Save Answer' },
  { id: 'webhook', label: '🔌 Call API' },
  { id: 'chain', label: '⛓️ Auto-continue' },
  { id: 'dynamic_list', label: '📡 Dynamic List' },
  { id: 'dynamic_response', label: '📦 Item Details' },
  { id: 'order_handler', label: '🛒 Order Handler' },
];

const S = {
  page: { background: 'var(--bg-page)', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  input: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const },
  card: { background: 'var(--bg-card)', borderRadius: '14px', border: '1px solid var(--bg-element)', padding: '24px', marginBottom: '16px' } as React.CSSProperties,
  responseCard: { background: 'var(--bg-page)', borderRadius: '10px', border: '1px solid var(--card-border, var(--bg-element))', padding: '16px', marginBottom: '12px' } as React.CSSProperties,
  label: { display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '6px' },
  btnPrimary: { padding: '9px 20px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' } as React.CSSProperties,
  btnGhost: { padding: '7px 14px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--bg-element)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnDanger: { padding: '6px 12px', background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
};

let _idCounter = 0;
const nextId = () => `r${Date.now()}_${_idCounter++}`;

export default function FacebookBuilder() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const isTemplate = window.location.pathname.includes('/template-builder/');
  const [botName, setBotName] = useState('My Facebook Bot');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'triggers' | 'menu' | 'settings'>('triggers');

  const [getStarted, setGetStarted] = useState('Hi! Welcome. How can I help you today?');
  const [triggers, setTriggers] = useState<FacebookTrigger[]>([
    { trigger: 'hi', responses: [{ id: nextId(), type: 'text', content: 'Hello! How can I help you?' }] },
  ]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  useEffect(() => { loadFlow(); }, [botId]);

  const loadFlow = async () => {
    if (!botId) { setLoading(false); return; }
    try {
      const [botSnap, flowSnap] = await Promise.all([
        get(ref(db, `bots/${botId}`)),
        get(ref(db, isTemplate ? `templates/${botId}` : `flows/${botId}`)),
      ]);
      if (botSnap.exists()) setBotName(botSnap.val().businessName || 'My Facebook Bot');
      if (flowSnap.exists()) {
        const d = flowSnap.val();
        if (d.getStarted) setGetStarted(d.getStarted);
        if (Array.isArray(d.triggers)) setTriggers(d.triggers.map(migrateTrigger));
        if (d.menuItems) setMenuItems(d.menuItems);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveFlow = async () => {
    if (!auth.currentUser) { alert('Login required'); return; }
    setSaving(true);
    try {
      await set(ref(db, isTemplate ? `templates/${botId}` : `flows/${botId}`), {
        getStarted, triggers, menuItems,
        botId, userId: auth.currentUser.uid, updatedAt: Date.now(),
      });

      // Push the Get Started button + persistent menu to the connected Page —
      // saving here alone never reaches Meta on its own. Not applicable to
      // templates (no bot/Page attached yet).
      if (!isTemplate) {
        try {
          const apiBase = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
          const idToken = await auth.currentUser.getIdToken();
          const r = await fetch(`${apiBase}/api/bots/${botId}/facebook-menu-sync`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${idToken}` },
          });
          const data = await r.json();
          if (!data.success) console.warn('Facebook menu sync failed:', data.error);
        } catch (e) { console.warn('Facebook menu sync failed:', e); }
      }

      alert('Saved!');
    } catch (e) { alert('Error: ' + e); }
    finally { setSaving(false); }
  };

  const addTrigger = () =>
    setTriggers(t => [...t, { trigger: 'new_trigger', responses: [{ id: nextId(), type: 'text', content: 'Response text' }] }]);

  const updateTriggerWord = (idx: number, value: string) => {
    const updated = [...triggers];
    updated[idx] = { ...updated[idx], trigger: value };
    setTriggers(updated);
  };

  const deleteTrigger = (idx: number) => setTriggers(t => t.filter((_, i) => i !== idx));

  const addResponse = (tIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses = [...updated[tIdx].responses, { id: nextId(), type: 'text', content: '' }];
    setTriggers(updated);
  };

  const deleteResponse = (tIdx: number, rIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses = updated[tIdx].responses.filter((_, i) => i !== rIdx);
    setTriggers(updated);
  };

  const updateResponse = (tIdx: number, rIdx: number, field: keyof ResponseItem, value: any) => {
    const updated = [...triggers];
    const responses = [...updated[tIdx].responses];
    responses[rIdx] = { ...responses[rIdx], [field]: value };
    updated[tIdx] = { ...updated[tIdx], responses };
    setTriggers(updated);
  };

  const addAskResponse = (tIdx: number, rIdx: number) => {
    const resp = triggers[tIdx].responses[rIdx];
    const askResponses = resp.askResponses || [];
    updateResponse(tIdx, rIdx, 'askResponses', [...askResponses, { id: nextId(), type: 'text', content: '' }]);
  };

  const updateAskResponse = (tIdx: number, rIdx: number, fIdx: number, field: keyof AskFollowUp, value: any) => {
    const resp = triggers[tIdx].responses[rIdx];
    const askResponses = [...(resp.askResponses || [])];
    askResponses[fIdx] = { ...askResponses[fIdx], [field]: value };
    updateResponse(tIdx, rIdx, 'askResponses', askResponses);
  };

  const deleteAskResponse = (tIdx: number, rIdx: number, fIdx: number) => {
    const resp = triggers[tIdx].responses[rIdx];
    updateResponse(tIdx, rIdx, 'askResponses', (resp.askResponses || []).filter((_, i) => i !== fIdx));
  };

  const addQuickReply = (tIdx: number, rIdx: number) => {
    const resp = triggers[tIdx].responses[rIdx];
    const buttons = resp.buttons || [];
    if (buttons.length >= 13) { alert('Max 13 quick replies (Meta\'s real Messenger limit)'); return; }
    updateResponse(tIdx, rIdx, 'buttons', [...buttons, { id: nextId(), title: 'Option', payload: 'option_payload' }]);
  };

  const addListRow = (tIdx: number, rIdx: number) => {
    const resp = triggers[tIdx].responses[rIdx];
    const rows = resp.listRows || [];
    if (rows.length >= 13) { alert('Max 13 list items (Meta\'s real Messenger quick-reply limit)'); return; }
    updateResponse(tIdx, rIdx, 'listRows', [...rows, { id: nextId(), title: 'Item', payload: 'item_payload' }]);
  };

  const addCard = (tIdx: number, rIdx: number) => {
    const resp = triggers[tIdx].responses[rIdx];
    const cards = resp.cards || [];
    if (cards.length >= 10) { alert('Max 10 cards (Messenger carousel limit)'); return; }
    updateResponse(tIdx, rIdx, 'cards', [...cards, { id: nextId(), title: 'Card title', buttons: [] }]);
  };

  const updateCard = (tIdx: number, rIdx: number, cIdx: number, patch: Partial<GenericCard>) => {
    const resp = triggers[tIdx].responses[rIdx];
    const cards = [...(resp.cards || [])];
    cards[cIdx] = { ...cards[cIdx], ...patch };
    updateResponse(tIdx, rIdx, 'cards', cards);
  };

  const addCardButton = (tIdx: number, rIdx: number, cIdx: number) => {
    const resp = triggers[tIdx].responses[rIdx];
    const card = (resp.cards || [])[cIdx];
    if ((card.buttons || []).length >= 3) { alert('Max 3 buttons per card'); return; }
    updateCard(tIdx, rIdx, cIdx, { buttons: [...(card.buttons || []), { id: nextId(), title: 'Learn more', url: '' }] });
  };

  const addMenuItem = () =>
    setMenuItems(m => [...m, { id: Date.now().toString(), title: 'Menu Item', payload: 'menu_payload', type: 'postback' }]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-page)' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading...</div>
    </div>
  );

  return (
    <>
      <SEO title={`Edit Facebook Bot | ${botName}`} description="" keywords="" type="website" noindex />
      <div style={S.page}>

        {/* Top bar */}
        <div style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--bg-element)', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button onClick={() => navigate(isTemplate ? '/templates' : '/dashboard')} style={S.btnGhost}>← {isTemplate ? 'Templates' : 'Dashboard'}</button>
            <span style={{ fontSize: '18px' }}>📘</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '700' }}>{botName}</span>
          </div>
          <button onClick={saveFlow} disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : '💾 Save'}
          </button>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '1px solid var(--bg-element)', background: 'var(--bg-card)', padding: '0 24px', display: 'flex', gap: '4px' }}>
          {([
            ['triggers', '💬 Auto-replies'],
            ['menu', '☰ Persistent Menu'],
            ['settings', '⚙️ Settings'],
          ] as const).map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '12px 18px', background: 'none', border: 'none', cursor: 'pointer',
              color: activeTab === tab ? '#5B4FE8' : 'var(--text-muted)',
              fontSize: '13px', fontWeight: activeTab === tab ? '700' : '400',
              borderBottom: activeTab === tab ? '2px solid #5B4FE8' : '2px solid transparent',
            }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '28px 20px' }}>

          {/* ── AUTO-REPLIES ── */}
          {activeTab === 'triggers' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700' }}>Auto-replies</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginTop: '3px' }}>When someone sends a keyword, your bot replies automatically — add multiple responses to send them in sequence</div>
                </div>
                <button onClick={addTrigger} style={S.btnPrimary}>+ Add reply</button>
              </div>

              {triggers.map((t, tIdx) => (
                <div key={tIdx} style={S.card}>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '18px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>When user types</label>
                      <input
                        value={t.trigger}
                        onChange={e => updateTriggerWord(tIdx, e.target.value.toLowerCase())}
                        style={S.input}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button onClick={() => deleteTrigger(tIdx)} style={S.btnDanger}>Delete</button>
                    </div>
                  </div>

                  {t.responses.map((resp, rIdx) => (
                    <div key={resp.id} style={S.responseCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {RESPONSE_TYPES.map(rt => (
                            <button key={rt.id} onClick={() => updateResponse(tIdx, rIdx, 'type', rt.id)}
                              style={{
                                padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '600',
                                background: resp.type === rt.id ? '#5B4FE8' : 'var(--bg-element)',
                                color: resp.type === rt.id ? 'white' : 'var(--text-muted)',
                              }}>{rt.label}</button>
                          ))}
                        </div>
                        {t.responses.length > 1 && (
                          <button onClick={() => deleteResponse(tIdx, rIdx)} style={S.btnDanger}>✕</button>
                        )}
                      </div>

                      {/* ── TEXT / BUTTONS / LIST / REQUEST_AGENT all share a content textarea ── */}
                      {(resp.type === 'text' || resp.type === 'buttons' || resp.type === 'list' || resp.type === 'link' || resp.type === 'request_agent') && (
                        <textarea
                          value={resp.content}
                          onChange={e => updateResponse(tIdx, rIdx, 'content', e.target.value)}
                          rows={2}
                          placeholder={resp.type === 'link' ? 'Message shown above the link button' : resp.type === 'request_agent' ? "Confirmation message, e.g. \"Connecting you to a team member...\"" : 'Reply message'}
                          style={{ ...S.textarea, marginBottom: '10px' }}
                        />
                      )}
                      {resp.type === 'request_agent' && (
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                          Sends this message, then pauses the bot for this conversation and notifies you — the same handoff the dashboard's "Take Over" button uses. Reply from the Conversations tab when you're ready.
                        </div>
                      )}

                      {/* ── IMAGE ── */}
                      {resp.type === 'image' && (
                        <MediaPicker value={resp.content} onChange={url => updateResponse(tIdx, rIdx, 'content', url)} kind="image" placeholder="Image URL (https://...)" />
                      )}

                      {/* ── QUICK REPLY BUTTONS (max 3) ── */}
                      {resp.type === 'buttons' && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={S.label}>Quick replies (max 13)</label>
                            <button onClick={() => addQuickReply(tIdx, rIdx)} style={S.btnGhost}>+ Add</button>
                          </div>
                          {(resp.buttons || []).map((btn, bIdx) => (
                            <div key={btn.id} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                              <input
                                value={btn.title}
                                onChange={e => {
                                  const buttons = [...(resp.buttons || [])];
                                  buttons[bIdx] = { ...buttons[bIdx], title: e.target.value };
                                  updateResponse(tIdx, rIdx, 'buttons', buttons);
                                }}
                                placeholder="Button text (max 20 chars)"
                                maxLength={20}
                                style={{ ...S.input, flex: 1 }}
                              />
                              <input
                                value={btn.payload}
                                onChange={e => {
                                  const buttons = [...(resp.buttons || [])];
                                  buttons[bIdx] = { ...buttons[bIdx], payload: e.target.value };
                                  updateResponse(tIdx, rIdx, 'buttons', buttons);
                                }}
                                placeholder="Payload (trigger word)"
                                style={{ ...S.input, flex: 1 }}
                              />
                              <button
                                onClick={() => updateResponse(tIdx, rIdx, 'buttons', (resp.buttons || []).filter((_, i) => i !== bIdx))}
                                style={S.btnDanger}
                              >✕</button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ── LIST (sent as quick replies — Messenger has no native list UI) ── */}
                      {resp.type === 'list' && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={S.label}>List items (max 13 — sent as quick replies)</label>
                            <button onClick={() => addListRow(tIdx, rIdx)} style={S.btnGhost}>+ Add</button>
                          </div>
                          {(resp.listRows || []).map((row, lIdx) => (
                            <div key={row.id} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                              <input
                                value={row.title}
                                onChange={e => {
                                  const rows = [...(resp.listRows || [])];
                                  rows[lIdx] = { ...rows[lIdx], title: e.target.value };
                                  updateResponse(tIdx, rIdx, 'listRows', rows);
                                }}
                                placeholder="Item title (max 20 chars)"
                                maxLength={20}
                                style={{ ...S.input, flex: 1 }}
                              />
                              <input
                                value={row.payload}
                                onChange={e => {
                                  const rows = [...(resp.listRows || [])];
                                  rows[lIdx] = { ...rows[lIdx], payload: e.target.value };
                                  updateResponse(tIdx, rIdx, 'listRows', rows);
                                }}
                                placeholder="Payload (trigger word)"
                                style={{ ...S.input, flex: 1 }}
                              />
                              <button
                                onClick={() => updateResponse(tIdx, rIdx, 'listRows', (resp.listRows || []).filter((_, i) => i !== lIdx))}
                                style={S.btnDanger}
                              >✕</button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ── LINK BUTTON (up to 3) ── */}
                      {resp.type === 'link' && (() => {
                        // Seed from the legacy single url/linkLabel fields the
                        // first time this is rendered, so flows saved before
                        // multi-button support still show their one button.
                        const linkButtons: LinkButton[] = resp.linkButtons?.length
                          ? resp.linkButtons
                          : (resp.url ? [{ id: 'legacy', title: resp.linkLabel || 'Learn more', url: resp.url }] : []);
                        return (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <label style={S.label}>Buttons (max 3)</label>
                              <button
                                onClick={() => {
                                  if (linkButtons.length >= 3) { alert('Max 3 buttons'); return; }
                                  updateResponse(tIdx, rIdx, 'linkButtons', [...linkButtons, { id: nextId(), title: 'Learn more', url: '' }]);
                                }}
                                style={S.btnGhost}
                              >+ Add</button>
                            </div>
                            {linkButtons.map((btn, bIdx) => (
                              <div key={btn.id} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                                <input
                                  value={btn.title}
                                  onChange={e => {
                                    const next = [...linkButtons];
                                    next[bIdx] = { ...next[bIdx], title: e.target.value };
                                    updateResponse(tIdx, rIdx, 'linkButtons', next);
                                  }}
                                  placeholder="Button label"
                                  style={{ ...S.input, flex: 1 }}
                                />
                                <input
                                  value={btn.url}
                                  onChange={e => {
                                    const next = [...linkButtons];
                                    next[bIdx] = { ...next[bIdx], url: e.target.value };
                                    updateResponse(tIdx, rIdx, 'linkButtons', next);
                                  }}
                                  placeholder="URL (https://...)"
                                  style={{ ...S.input, flex: 1 }}
                                />
                                <button
                                  onClick={() => updateResponse(tIdx, rIdx, 'linkButtons', linkButtons.filter((_, i) => i !== bIdx))}
                                  style={S.btnDanger}
                                >✕</button>
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* ── CAROUSEL (generic template, up to 10 cards) ── */}
                      {resp.type === 'generic_template' && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={S.label}>Cards (max 10, swipeable)</label>
                            <button onClick={() => addCard(tIdx, rIdx)} style={S.btnGhost}>+ Add card</button>
                          </div>
                          {(resp.cards || []).map((card, cIdx) => (
                            <div key={card.id} style={{ ...S.responseCard, background: 'var(--bg-card)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <label style={S.label}>Card {cIdx + 1}</label>
                                <button
                                  onClick={() => updateResponse(tIdx, rIdx, 'cards', (resp.cards || []).filter((_, i) => i !== cIdx))}
                                  style={S.btnDanger}
                                >✕</button>
                              </div>
                              <input
                                value={card.title}
                                onChange={e => updateCard(tIdx, rIdx, cIdx, { title: e.target.value })}
                                placeholder="Title (max 80 chars)"
                                maxLength={80}
                                style={{ ...S.input, marginBottom: '8px' }}
                              />
                              <input
                                value={card.subtitle || ''}
                                onChange={e => updateCard(tIdx, rIdx, cIdx, { subtitle: e.target.value })}
                                placeholder="Subtitle (max 80 chars)"
                                maxLength={80}
                                style={{ ...S.input, marginBottom: '8px' }}
                              />
                              <div style={{ marginBottom: '8px' }}>
                                <MediaPicker value={card.imageUrl || ''} onChange={url => updateCard(tIdx, rIdx, cIdx, { imageUrl: url })} kind="image" placeholder="Card image URL (https://...)" />
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <label style={S.label}>Buttons (max 3)</label>
                                <button onClick={() => addCardButton(tIdx, rIdx, cIdx)} style={S.btnGhost}>+ Add</button>
                              </div>
                              {(card.buttons || []).map((btn, bIdx) => (
                                <div key={btn.id} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                                  <select
                                    value={btn.type || 'web_url'}
                                    onChange={e => {
                                      const buttons = [...card.buttons];
                                      buttons[bIdx] = { ...buttons[bIdx], type: e.target.value as 'web_url' | 'phone_number' };
                                      updateCard(tIdx, rIdx, cIdx, { buttons });
                                    }}
                                    style={{ ...S.input, flex: '0 0 90px', padding: '9px 6px' }}
                                  >
                                    <option value="web_url">Link</option>
                                    <option value="phone_number">Call</option>
                                  </select>
                                  <input
                                    value={btn.title}
                                    onChange={e => {
                                      const buttons = [...card.buttons];
                                      buttons[bIdx] = { ...buttons[bIdx], title: e.target.value };
                                      updateCard(tIdx, rIdx, cIdx, { buttons });
                                    }}
                                    placeholder="Button label"
                                    style={{ ...S.input, flex: 1 }}
                                  />
                                  <input
                                    value={btn.url}
                                    onChange={e => {
                                      const buttons = [...card.buttons];
                                      buttons[bIdx] = { ...buttons[bIdx], url: e.target.value };
                                      updateCard(tIdx, rIdx, cIdx, { buttons });
                                    }}
                                    placeholder={btn.type === 'phone_number' ? 'Phone (+263...)' : 'URL (https://...)'}
                                    style={{ ...S.input, flex: 1 }}
                                  />
                                  <button
                                    onClick={() => updateCard(tIdx, rIdx, cIdx, { buttons: card.buttons.filter((_, i) => i !== bIdx) })}
                                    style={S.btnDanger}
                                  >✕</button>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ── ASK & SAVE ANSWER ── */}
                      {resp.type === 'ask' && (
                        <div>
                          <input
                            value={resp.content}
                            onChange={e => updateResponse(tIdx, rIdx, 'content', e.target.value)}
                            placeholder="Question to ask e.g. What is your name?"
                            style={{ ...S.input, marginBottom: '8px' }}
                          />
                          <input
                            value={resp.askVariable || ''}
                            onChange={e => updateResponse(tIdx, rIdx, 'askVariable', e.target.value)}
                            placeholder="Variable name (e.g. name, phone, email)"
                            style={{ ...S.input, marginBottom: '8px' }}
                          />
                          {resp.askVariable && (
                            <div style={{ fontSize: '11px', color: '#f59e0b', padding: '8px 12px', background: '#f59e0b10', borderRadius: '8px', marginBottom: '12px' }}>
                              Answer saved as <strong>{`{{${resp.askVariable}}}`}</strong>
                            </div>
                          )}
                          <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>AFTER ANSWER, SEND:</div>
                          {(resp.askResponses || []).map((follow, fIdx) => (
                            <div key={follow.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                              <select
                                value={follow.type}
                                onChange={e => updateAskResponse(tIdx, rIdx, fIdx, 'type', e.target.value)}
                                style={{ ...S.input, flex: '0 0 100px' }}
                              >
                                <option value="text">Text</option>
                                <option value="buttons">Buttons</option>
                                <option value="list">List</option>
                              </select>
                              <input
                                value={follow.content}
                                onChange={e => updateAskResponse(tIdx, rIdx, fIdx, 'content', e.target.value)}
                                placeholder="Response text"
                                style={{ ...S.input, flex: 1 }}
                              />
                              <button onClick={() => deleteAskResponse(tIdx, rIdx, fIdx)} style={S.btnDanger}>✕</button>
                            </div>
                          ))}
                          <button onClick={() => addAskResponse(tIdx, rIdx)} style={S.btnGhost}>+ Add follow-up response</button>
                        </div>
                      )}

                      {/* ── CALL API (WEBHOOK) ── */}
                      {resp.type === 'webhook' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <input
                            value={resp.content}
                            onChange={e => updateResponse(tIdx, rIdx, 'content', e.target.value)}
                            placeholder="API URL"
                            style={S.input}
                          />
                          <select
                            value={resp.webhookMethod || 'GET'}
                            onChange={e => updateResponse(tIdx, rIdx, 'webhookMethod', e.target.value)}
                            style={{ ...S.input, appearance: 'none' }}
                          >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                          </select>
                          <textarea
                            value={resp.webhookBody || ''}
                            onChange={e => updateResponse(tIdx, rIdx, 'webhookBody', e.target.value)}
                            rows={2}
                            placeholder="POST Body (JSON)"
                            style={S.textarea}
                          />
                          <input
                            value={resp.webhookResponsePath || ''}
                            onChange={e => updateResponse(tIdx, rIdx, 'webhookResponsePath', e.target.value)}
                            placeholder="Response path (e.g. status)"
                            style={S.input}
                          />
                          <input
                            value={resp.webhookSuccessMessage || ''}
                            onChange={e => updateResponse(tIdx, rIdx, 'webhookSuccessMessage', e.target.value)}
                            placeholder="Success message (use {{data}} for the extracted value)"
                            style={S.input}
                          />
                          <input
                            value={resp.webhookErrorMessage || ''}
                            onChange={e => updateResponse(tIdx, rIdx, 'webhookErrorMessage', e.target.value)}
                            placeholder="Error message"
                            style={S.input}
                          />
                        </div>
                      )}

                      {/* ── AUTO-CONTINUE (CHAIN) ── */}
                      {resp.type === 'chain' && (
                        <div style={{ background: '#14b8a610', border: '1px solid #14b8a630', borderRadius: '8px', padding: '12px' }}>
                          <div style={{ color: '#14b8a6', fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>Auto-triggers another reply after a delay</div>
                          <input
                            value={resp.chainTrigger || ''}
                            onChange={e => updateResponse(tIdx, rIdx, 'chainTrigger', e.target.value)}
                            placeholder="Trigger word to auto-send"
                            style={{ ...S.input, marginBottom: '8px' }}
                          />
                          <input
                            type="number"
                            value={resp.chainDelay || 1}
                            onChange={e => updateResponse(tIdx, rIdx, 'chainDelay', parseInt(e.target.value) || 1)}
                            placeholder="Delay in seconds"
                            style={S.input}
                          />
                          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '6px' }}>
                            Bot sends "{resp.chainTrigger || 'trigger'}" automatically after {resp.chainDelay || 1}s
                          </div>
                        </div>
                      )}

                      {/* ── DYNAMIC LIST ── */}
                      {resp.type === 'dynamic_list' && (
                        <div style={{ background: '#0ea5e910', border: '1px solid #0ea5e930', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ color: '#0ea5e9', fontSize: '12px', fontWeight: '600' }}>Fetches items from an API and shows as quick replies (max 13)</div>
                          <input value={resp.apiUrl || ''} onChange={e => updateResponse(tIdx, rIdx, 'apiUrl', e.target.value)} placeholder="API URL (e.g. https://api.example.com/products)" style={S.input} />
                          <select value={resp.apiMethod || 'GET'} onChange={e => updateResponse(tIdx, rIdx, 'apiMethod', e.target.value)} style={{ ...S.input, appearance: 'none' }}>
                            <option value="GET">GET</option><option value="POST">POST</option>
                          </select>
                          {resp.apiMethod === 'POST' && <textarea value={resp.apiBody || ''} onChange={e => updateResponse(tIdx, rIdx, 'apiBody', e.target.value)} rows={2} placeholder="POST Body (JSON)" style={S.textarea} />}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <input value={resp.itemTitlePath || ''} onChange={e => updateResponse(tIdx, rIdx, 'itemTitlePath', e.target.value)} placeholder="Title path (e.g. name)" style={S.input} />
                            <input value={resp.itemPayloadPath || ''} onChange={e => updateResponse(tIdx, rIdx, 'itemPayloadPath', e.target.value)} placeholder="Payload path (e.g. id)" style={S.input} />
                          </div>
                          <textarea value={resp.content || ''} onChange={e => updateResponse(tIdx, rIdx, 'content', e.target.value)} rows={2} placeholder="Message above the list" style={S.textarea} />
                        </div>
                      )}

                      {/* ── DYNAMIC RESPONSE (ITEM DETAILS) ── */}
                      {resp.type === 'dynamic_response' && (
                        <FacebookDynamicResponseEditor
                          config={resp.dynamicResponseConfig}
                          onUpdate={cfg => updateResponse(tIdx, rIdx, 'dynamicResponseConfig', cfg)}
                        />
                      )}

                      {/* ── ORDER HANDLER ── */}
                      {resp.type === 'order_handler' && (
                        <FacebookOrderHandlerEditor
                          config={resp.orderHandlerConfig}
                          onUpdate={cfg => updateResponse(tIdx, rIdx, 'orderHandlerConfig', cfg)}
                        />
                      )}
                    </div>
                  ))}

                  <button onClick={() => addResponse(tIdx)} style={S.btnGhost}>+ Add another response</button>
                </div>
              ))}
            </>
          )}

          {/* ── PERSISTENT MENU ── */}
          {activeTab === 'menu' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700' }}>Persistent menu</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginTop: '3px' }}>Always-visible menu in the Messenger chat bar (max 3 items)</div>
                </div>
                <button
                  onClick={addMenuItem}
                  disabled={menuItems.length >= 3}
                  style={{ ...S.btnPrimary, opacity: menuItems.length >= 3 ? 0.4 : 1 }}
                >
                  + Add item
                </button>
              </div>

              {menuItems.length === 0 && (
                <div style={{ ...S.card, textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '12px' }}>☰</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '14px', marginBottom: '16px' }}>
                    No menu items yet. Add up to 3 items that are always visible in Messenger.
                  </div>
                  <button onClick={addMenuItem} style={S.btnPrimary}>Add first item</button>
                </div>
              )}

              {menuItems.map((item, idx) => (
                <div key={item.id} style={S.card}>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Label</label>
                      <input
                        value={item.title}
                        onChange={e => { const m = [...menuItems]; m[idx].title = e.target.value; setMenuItems(m); }}
                        placeholder="e.g. Book Appointment"
                        style={S.input}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Type</label>
                      <select
                        value={item.type}
                        onChange={e => { const m = [...menuItems]; m[idx].type = e.target.value as any; setMenuItems(m); }}
                        style={{ ...S.input, appearance: 'none' }}
                      >
                        <option value="postback">Trigger a reply</option>
                        <option value="web_url">Open URL</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button onClick={() => setMenuItems(m => m.filter((_, i) => i !== idx))} style={S.btnDanger}>
                        Delete
                      </button>
                    </div>
                  </div>

                  {item.type === 'postback' ? (
                    <input
                      value={item.payload}
                      onChange={e => { const m = [...menuItems]; m[idx].payload = e.target.value; setMenuItems(m); }}
                      placeholder="Payload (matches a trigger word above)"
                      style={S.input}
                    />
                  ) : (
                    <input
                      value={item.url || ''}
                      onChange={e => { const m = [...menuItems]; m[idx].url = e.target.value; setMenuItems(m); }}
                      placeholder="URL to open (https://...)"
                      style={S.input}
                    />
                  )}
                </div>
              ))}
            </>
          )}

          {/* ── SETTINGS ── */}
          {activeTab === 'settings' && (
            <>
              <div style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700', marginBottom: '20px' }}>Settings</div>
              <div style={S.card}>
                <label style={S.label}>Get started message</label>
                <textarea
                  value={getStarted}
                  onChange={e => setGetStarted(e.target.value)}
                  rows={3}
                  placeholder="Sent when someone clicks 'Get Started' for the first time"
                  style={{ ...S.textarea, marginBottom: '8px' }}
                />
                <div style={{ fontSize: '11px', color: 'var(--text-faint)' }}>
                  This is the first message shown when a new user starts a conversation with your page. Also add a trigger word "hi" (or "get_started") above with the same welcome content — Messenger's Get Started button sends that as the first message.
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   DYNAMIC RESPONSE EDITOR (item details, shown after picking from a dynamic list)
───────────────────────────────────────────── */
function FacebookDynamicResponseEditor({ config, onUpdate }: { config?: DynamicResponseConfig; onUpdate: (cfg: DynamicResponseConfig) => void }) {
  const cfg = config || {};
  const update = (field: keyof DynamicResponseConfig, value: any) => onUpdate({ ...cfg, [field]: value });

  return (
    <div style={{ background: '#f9731610', border: '1px solid #f9731630', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ color: '#f97316', fontSize: '12px', fontWeight: '600' }}>Fetches and displays details of an item selected from a Dynamic List</div>

      <input value={cfg.apiUrl || ''} onChange={e => update('apiUrl', e.target.value)} placeholder="API URL (e.g. https://api.example.com/item/{{id}})" style={S.input} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <select value={cfg.apiMethod || 'GET'} onChange={e => update('apiMethod', e.target.value)} style={{ ...S.input, appearance: 'none' }}>
          <option value="GET">GET</option><option value="POST">POST</option>
        </select>
        <input value={cfg.apiBody || ''} onChange={e => update('apiBody', e.target.value)} placeholder="POST body (JSON)" style={S.input} />
      </div>

      <div style={{ borderTop: '1px solid var(--bg-element)', paddingTop: '10px' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>MESSAGE FORMAT</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <input value={cfg.imagePath || ''} onChange={e => update('imagePath', e.target.value)} placeholder="Image path (optional)" style={S.input} />
          <input value={cfg.titlePath || ''} onChange={e => update('titlePath', e.target.value)} placeholder="Title path *" style={S.input} />
          <input value={cfg.categoryPath || ''} onChange={e => update('categoryPath', e.target.value)} placeholder="Category path (optional)" style={S.input} />
          <input value={cfg.metaPath || ''} onChange={e => update('metaPath', e.target.value)} placeholder="Meta path (price/date/rating)" style={S.input} />
          <input value={cfg.metaLabel || ''} onChange={e => update('metaLabel', e.target.value)} placeholder="Meta label (e.g. Price)" style={S.input} />
          <input value={cfg.descriptionPath || ''} onChange={e => update('descriptionPath', e.target.value)} placeholder="Description path *" style={S.input} />
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600' }}>EXTRA FIELDS</div>
          <button onClick={() => update('customFields', [...(cfg.customFields || []), { id: Date.now().toString(), label: 'New Field', path: '', emoji: '📌' }])} style={S.btnGhost}>+ Add field</button>
        </div>
        {(cfg.customFields || []).map((field, fIdx) => (
          <div key={field.id} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
            <input value={field.emoji || '📌'} onChange={e => { const f = [...(cfg.customFields || [])]; f[fIdx] = { ...f[fIdx], emoji: e.target.value }; update('customFields', f); }} style={{ ...S.input, width: '50px' }} />
            <input value={field.label} onChange={e => { const f = [...(cfg.customFields || [])]; f[fIdx] = { ...f[fIdx], label: e.target.value }; update('customFields', f); }} placeholder="Label" style={{ ...S.input, flex: 1 }} />
            <input value={field.path} onChange={e => { const f = [...(cfg.customFields || [])]; f[fIdx] = { ...f[fIdx], path: e.target.value }; update('customFields', f); }} placeholder="JSON path" style={{ ...S.input, flex: 2 }} />
            <button onClick={() => update('customFields', (cfg.customFields || []).filter((_, i) => i !== fIdx))} style={S.btnDanger}>✕</button>
          </div>
        ))}
      </div>

      <div>
        <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>BUTTONS (max 3)</div>
        {[1, 2, 3].map(n => (
          <div key={n} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
            <input value={(cfg as any)[`button${n}Title`] || ''} onChange={e => update(`button${n}Title` as any, e.target.value)} placeholder={`Button ${n} text`} style={S.input} />
            <input value={(cfg as any)[`button${n}Payload`] || ''} onChange={e => update(`button${n}Payload` as any, e.target.value)} placeholder="Payload (use {{id}})" style={S.input} />
          </div>
        ))}
      </div>

      <textarea value={cfg.fallbackMessage || ''} onChange={e => update('fallbackMessage', e.target.value)} rows={2} placeholder="Fallback message if no data found" style={S.textarea} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   ORDER HANDLER EDITOR
───────────────────────────────────────────── */
function FacebookOrderHandlerEditor({ config, onUpdate }: { config?: OrderHandlerConfig; onUpdate: (cfg: OrderHandlerConfig) => void }) {
  const cfg = config || { collectName: false, collectPhone: false, collectEmail: false, collectAddress: false, askQuantity: false, action: 'custom_message' as const, confirmationMessage: '' };
  const update = (changes: Partial<OrderHandlerConfig>) => onUpdate({ ...cfg, ...changes });

  return (
    <div style={{ background: '#84cc1610', border: '1px solid #84cc1630', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ color: '#84cc16', fontSize: '12px', fontWeight: '600' }}>Collect customer info and process orders</div>

      <div>
        <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>COLLECT FROM CUSTOMER</div>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {(['collectName', 'collectPhone', 'collectEmail', 'collectAddress'] as const).map(field => (
            <label key={field} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={cfg[field] || false} onChange={e => update({ [field]: e.target.checked })} />
              {field.replace('collect', '')}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={cfg.askQuantity || false} onChange={e => update({ askQuantity: e.target.checked })} />
          Ask for quantity
        </label>
        {cfg.askQuantity && (
          <input value={cfg.quantityLabel || ''} onChange={e => update({ quantityLabel: e.target.value })} placeholder="Question (e.g. How many would you like?)" style={{ ...S.input, marginTop: '8px' }} />
        )}
      </div>

      <div>
        <label style={S.label}>After collection</label>
        <select value={cfg.action || 'custom_message'} onChange={e => update({ action: e.target.value as any })} style={{ ...S.input, appearance: 'none' }}>
          <option value="payment_link">Generate payment link</option>
          <option value="admin_notification">Notify admin</option>
          <option value="api_call">Call API</option>
          <option value="custom_message">Send custom message</option>
        </select>
      </div>

      {cfg.action === 'payment_link' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input value={cfg.paymentLinkUrl || ''} onChange={e => update({ paymentLinkUrl: e.target.value })} placeholder="Payment URL (use {{id}}, {{price}}, {{name}})" style={S.input} />
          <input value={cfg.paymentLinkLabel || ''} onChange={e => update({ paymentLinkLabel: e.target.value })} placeholder="Button text (e.g. Pay Now)" style={S.input} />
        </div>
      )}

      {cfg.action === 'admin_notification' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '11px', color: '#f59e0b', background: '#f59e0b10', border: '1px solid #f59e0b30', borderRadius: '6px', padding: '8px 10px' }}>
            ⚠️ Admin WhatsApp number notifications aren't supported for Facebook bots yet — leave this blank and the order will still be saved, just without a WhatsApp alert to you.
          </div>
          <input value={cfg.adminPhone || ''} onChange={e => update({ adminPhone: e.target.value })} placeholder="Admin WhatsApp number (not yet supported for Facebook)" style={S.input} disabled />
          <textarea value={cfg.adminMessage || ''} onChange={e => update({ adminMessage: e.target.value })} rows={2} placeholder="Admin message (use {{product}}, {{name}}, etc.)" style={S.textarea} disabled />
        </div>
      )}

      {cfg.action === 'api_call' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input value={cfg.apiUrl || ''} onChange={e => update({ apiUrl: e.target.value })} placeholder="API URL" style={S.input} />
          <select value={cfg.apiMethod || 'POST'} onChange={e => update({ apiMethod: e.target.value as any })} style={{ ...S.input, appearance: 'none' }}>
            <option value="POST">POST</option><option value="GET">GET</option>
          </select>
          <textarea value={cfg.apiBody || ''} onChange={e => update({ apiBody: e.target.value })} rows={2} placeholder="POST body (JSON) — use {{id}}, {{name}}, {{phone}}" style={S.textarea} />
          <input value={cfg.successMessage || ''} onChange={e => update({ successMessage: e.target.value })} placeholder="Success message" style={S.input} />
          <input value={cfg.errorMessage || ''} onChange={e => update({ errorMessage: e.target.value })} placeholder="Error message" style={S.input} />
        </div>
      )}

      {cfg.action === 'custom_message' && (
        <textarea value={cfg.customMessage || ''} onChange={e => update({ customMessage: e.target.value })} rows={2} placeholder="Custom message to send after collection" style={S.textarea} />
      )}

      <div>
        <label style={S.label}>Final confirmation message</label>
        <textarea value={cfg.confirmationMessage || ''} onChange={e => update({ confirmationMessage: e.target.value })} rows={2} placeholder="Thanks for your order! We'll be in touch shortly." style={S.textarea} />
      </div>
    </div>
  );
}
